import React, { useState, useEffect, useRef } from 'react';
import { ArrowDown, Sparkles, Film, Heart, Wand2 } from 'lucide-react';
import VideoShowcase from '../components/VideoShowcase';
import ContactForm from '../components/ContactForm';
import { portfolioVideos, heroVideo } from '../mockData';

const Home = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);
  
  // Refs for scroll reveal
  const heroVideoRef = useRef(null);
  const statementRef = useRef(null);
  const showcaseRef = useRef(null);
  const processRef = useRef(null);
  const clientsRef = useRef(null);
  const closingRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    const refs = [statementRef, showcaseRef, processRef, clientsRef, closingRef];
    refs.forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      refs.forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  const scrollToShowcase = () => {
    document.getElementById('showcase')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="home-page">
      {/* Header Navigation */}
      <header className="site-header">
        <div className="header-content">
          <div className="logo">AIadmitra – Films & Ads</div>
          <nav className="nav-links">
            <a href="#showcase" className="nav-link">Our Work</a>
            <button onClick={() => setIsContactOpen(true)} className="nav-cta">
              Let's Talk
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section - Video-First Opening Shot */}
      <section className="hero-section">
        <div className="hero-video-container">
          <video
            ref={heroVideoRef}
            autoPlay
            loop
            muted
            playsInline
            className="hero-video"
            poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1920 1080'%3E%3Crect width='1920' height='1080' fill='%23000000'/%3E%3C/svg%3E"
          >
            <source src={heroVideo.videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="hero-overlay" />
        </div>
        
        {/* Upper Text Area */}
        <div className="hero-brand-container">
          <h1 className="hero-title">
            Static Ads Are Over.
            <span className="hero-title-accent">Stories Are What People Remember.</span>
          </h1>
          <p className="hero-subtitle">
            We create cinematic AI films that feel human — not like ads.
          </p>
        </div>
        
        {/* Lower Brand Text Area - Always Visible */}
        <div className="hero-brand-text-container">
          <h2 className="hero-brand-title">
            AIADMITRA
            <br />
            FILMS & ADS
          </h2>
          <p className="hero-brand-tagline">Create Your Own Branded Videos</p>
        </div>
        
        {/* Fixed Bottom CTA Container */}
        <div className="hero-cta-container">
          <div className="hero-cta-wrapper">
            <button onClick={scrollToShowcase} className="btn-primary">
              Watch Our Work
            </button>
            <button onClick={() => setIsContactOpen(true)} className="btn-secondary">
              <span>Create a Film With Us</span>
            </button>
          </div>
        </div>
      </section>

      {/* Statement Section - Motion-Driven */}
      <section className="statement-section scroll-reveal" ref={statementRef}>
        <div className="statement-content">
          <div className="statement-lines">
            <div className="statement-line muted">Static images don't move people</div>
            <div className="statement-line muted">Loud ads are skipped</div>
            <div className="statement-line gold">Stories make brands unforgettable</div>
          </div>
          
          <div className="statement-manifesto">
            <p>
              We don't design ads. We craft cinematic moments using AI as a tool, not a shortcut.
            </p>
          </div>
        </div>
      </section>

      {/* Video Showcase Section */}
      <div id="showcase" className="scroll-reveal" ref={showcaseRef}>
        <VideoShowcase videos={portfolioVideos} />
      </div>

      {/* Process Section */}
      <section className="process-section scroll-reveal" ref={processRef}>
        <div className="container">
          <h2 className="section-title">How We Create Emotional Stories</h2>
          
          <div className="process-grid">
            <div className="process-card">
              <Heart className="process-icon" size={40} />
              <h3>Emotion First</h3>
              <p>What should the viewer feel? We start with the emotional destination, not the message.</p>
            </div>
            
            <div className="process-card">
              <Film className="process-icon" size={40} />
              <h3>Story Over Script</h3>
              <p>Short narratives, not slogans. Every frame carries meaning and momentum.</p>
            </div>
            
            <div className="process-card">
              <Sparkles className="process-icon" size={40} />
              <h3>AI as a Film Tool</h3>
              <p>Visuals, speed, flexibility — AI accelerates creativity without replacing it.</p>
            </div>
            
            <div className="process-card">
              <Wand2 className="process-icon" size={40} />
              <h3>Cinematic Direction</h3>
              <p>Pacing, mood, and realism. We direct every moment to feel authentic and memorable.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Clients Section */}
      <section className="clients-section scroll-reveal" ref={clientsRef}>
        <div className="container">
          <h2 className="section-title">Who This Is For</h2>
          
          <div className="clients-grid">
            <div className="client-card">
              <h3>Real Estate & Luxury Projects</h3>
              <p>Showcase properties through emotional storytelling, not static renders.</p>
            </div>
            
            <div className="client-card">
              <h3>Education & Institutions</h3>
              <p>Inspire prospective students with stories of transformation and possibility.</p>
            </div>
            
            <div className="client-card">
              <h3>Wellness & Lifestyle Brands</h3>
              <p>Create moments of calm, connection, and aspiration that resonate deeply.</p>
            </div>
            
            <div className="client-card">
              <h3>Consumer Brands & Founders</h3>
              <p>Build emotional equity with films that audiences want to share, not skip.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA Section */}
      <section className="closing-cta-section scroll-reveal" ref={closingRef}>
        <div className="closing-content">
          <h2 className="closing-title">
            Your Brand Doesn't Need Another Ad.
            <br />
            <span className="closing-accent">It Needs a Story.</span>
          </h2>
          <button onClick={() => setIsContactOpen(true)} className="btn-large">
            Let's Create a Film
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <h3>AIadmitra – Films & Ads</h3>
            <p className="footer-tagline">Where AI meets emotion.</p>
          </div>
          <div className="footer-contact">
            <h4>Contact</h4>
            <p><a href="mailto:hello@aiadmitra.com" className="footer-link">hello@aiadmitra.com</a></p>
            <p><a href="mailto:c.dubey@aiadmitra.com" className="footer-link">c.dubey@aiadmitra.com</a></p>
            <p><a href="https://aiadmitra.com" target="_blank" rel="noopener noreferrer" className="footer-link">aiadmitra.com</a></p>
            <p><a href="https://wa.me/919769805091" target="_blank" rel="noopener noreferrer" className="footer-link">WhatsApp: +91 9769805091</a></p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 Xfiber Services. All rights reserved.</p>
        </div>
      </footer>

      {/* Contact Form Modal */}
      <ContactForm isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </div>
  );
};

export default Home;
